import { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMatchRespondDetails, useMatchRespondMutation, type MatchRespondError } from '@/hooks/useTeacherMatchRespond';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle2, Clock, Loader2, ShieldAlert, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const FILTER_LABELS: Record<string, string> = {
  subjects: 'วิชา',
  educationStages: 'ระดับชั้น',
  courseTypes: 'ประเภทคอร์ส',
  specialized: 'ความเชี่ยวชาญ',
  teachingStyles: 'รูปแบบการสอน',
  styleLevels: 'ระดับการสอน',
  gender: 'เพศครู',
  personality: 'บุคลิก',
  trialLesson: 'การสอน Trial',
  branches: 'สาขา',
  studentProficiency: 'ระดับนักเรียน',
  university: 'มหาวิทยาลัย',
  major: 'สาขาวิชา',
  availabilitySlots: 'วัน-เวลาที่ต้องการ',
  taughtSelectedStudent: 'เคยสอนนักเรียนคนนี้',
  search: 'คำค้นหา',
};

const DAY_LABELS = ['', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.'];

function formatExpire(iso: string): string {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d);
  } catch { return iso; }
}

function renderValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (key === 'availabilitySlots' && Array.isArray(value)) {
    return value
      .map((s: any) => {
        const d = DAY_LABELS[s.day] || `?${s.day}`;
        const time = s.start && s.end ? ` ${s.start}-${s.end}` : '';
        return `${d}${time}`;
      })
      .join(', ');
  }
  if (key === 'taughtSelectedStudent') {
    if (value === 'yes') return 'เคยสอน';
    if (value === 'no') return 'ยังไม่เคยสอน';
    return '';
  }
  if (Array.isArray(value)) return value.length ? value.join(', ') : '';
  return String(value);
}

export default function TeacherMatchRespondPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const { data, isLoading, error } = useMatchRespondDetails(requestId);
  const respond = useMatchRespondMutation(requestId);
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [scheduleChecked, setScheduleChecked] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const isExpired = data ? new Date(data.expire_at).getTime() < now : false;

  const filterEntries = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.search_filters || {})
      .filter(([k]) => FILTER_LABELS[k])
      .map(([k, v]) => ({ key: k, label: FILTER_LABELS[k], value: renderValue(k, v) }))
      .filter((e) => e.value !== '');
  }, [data]);

  const lineDetails = useMemo(() => {
    const ld = (data?.search_filters as any)?._lineDetails;
    if (!ld || typeof ld !== 'object') return null;
    return ld as {
      nickname?: string; firstName?: string; lastName?: string; school?: string;
      trialTopic?: string; hours?: number | string | null; classType?: string; interSubType?: string;
      days?: string[]; times?: string[]; desiredStartDate?: string;
      parentExpectations?: string; parentConcerns?: string[]; parentConcernsOther?: string;
    };
  }, [data]);

  const ldEntries = useMemo(() => {
    if (!lineDetails) return [] as Array<{ label: string; value: string }>;
    const entries: Array<{ label: string; value: string }> = [];
    const fullName = [lineDetails.firstName, lineDetails.lastName].filter(Boolean).join(' ');
    if (lineDetails.nickname) entries.push({ label: 'ชื่อเล่น', value: lineDetails.nickname });
    if (fullName) entries.push({ label: 'ชื่อ-นามสกุล', value: fullName });
    if (lineDetails.school) entries.push({ label: 'โรงเรียน', value: lineDetails.school });
    if (lineDetails.trialTopic) entries.push({ label: 'หัวข้อเรียนครั้งแรก', value: lineDetails.trialTopic });
    const hoursStr = lineDetails.hours != null && lineDetails.hours !== '' ? `${lineDetails.hours} ชม.` : '';
    const typeStr = (lineDetails.classType || '') + (lineDetails.interSubType ? ` (${lineDetails.interSubType})` : '');
    if (hoursStr || typeStr) entries.push({ label: 'ชั่วโมง/ประเภท', value: [hoursStr, typeStr].filter(Boolean).join(' • ') });
    if (lineDetails.days?.length) entries.push({ label: 'วันที่ต้องการเรียน', value: lineDetails.days.join(', ') });
    if (lineDetails.times?.length) entries.push({ label: 'เวลาที่ต้องการเรียน', value: lineDetails.times.join(', ') });
    if (lineDetails.desiredStartDate) entries.push({ label: 'วันเริ่มเรียน', value: lineDetails.desiredStartDate });
    if (lineDetails.parentExpectations) entries.push({ label: 'ความคาดหวัง', value: lineDetails.parentExpectations });
    const concerns = [...(lineDetails.parentConcerns || [])];
    if (lineDetails.parentConcernsOther) concerns.push(lineDetails.parentConcernsOther);
    if (concerns.length) entries.push({ label: 'ปัญหาที่กังวล', value: concerns.join(', ') });
    return entries;
  }, [lineDetails]);

  const handleConfirm = () => {
    respond.mutate({ decision: 'confirm' }, {
      onSuccess: () => toast.success('ยืนยันรับงานเรียบร้อย ขอบคุณค่ะ'),
      onError: (e) => toast.error(errorMessage(e)),
    });
  };

  const handleDeny = () => {
    respond.mutate({ decision: 'deny', deny_reason: denyReason.trim() || undefined }, {
      onSuccess: () => {
        toast.success('บันทึกการปฏิเสธเรียบร้อย');
        setDenyOpen(false);
        setDenyReason('');
      },
      onError: (e) => toast.error(errorMessage(e)),
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> กำลังโหลดคำขอ...
        </div>
      </div>
    );
  }

  if (error) {
    const code = (error as MatchRespondError).error;
    const isAuth = code === 'not_authorized' || code === 'unauthenticated';
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" />
              {isAuth ? 'คุณไม่มีสิทธิ์เข้าถึงคำขอนี้' : 'ไม่สามารถโหลดคำขอ'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {isAuth
                ? 'คำขอนี้ไม่ได้ถูกส่งให้บัญชีของคุณ หรือบัญชีของคุณยังไม่ได้ผูกกับรหัสครู'
                : `เกิดข้อผิดพลาด: ${code || 'unknown'}`}
            </p>
            <Button asChild variant="outline"><Link to="/">กลับหน้าหลัก</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const respondedDisabled = isExpired || data.my_status !== 'pending' || data.status !== 'pending' || respond.isPending;

  return (
    <div className="min-h-screen bg-muted/30 p-4 flex justify-center">
      <div className="w-full max-w-2xl space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>คำขอจับคู่งานสอน</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Expire info */}
            <div className={`flex items-start gap-2 rounded-md border p-3 text-sm ${isExpired ? 'border-destructive/40 bg-destructive/5 text-destructive' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
              <Clock className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                {isExpired ? 'หมดเวลาตอบรับแล้ว' : 'กดยืนยัน/ปฏิเสธได้ภายใน'}
                <span className="font-semibold"> {formatExpire(data.expire_at)} น.</span>
              </div>
            </div>

            {/* Student */}
            <div className="rounded-md border p-3 space-y-1 text-sm">
              <div className="text-muted-foreground text-xs">นักเรียน</div>
              <div className="font-semibold">
                {data.student.display_name || '-'}
                {data.student.nickname ? ` (${data.student.nickname})` : ''}
              </div>
            </div>

            {/* Filter details */}
            <div className="rounded-md border p-3 space-y-2 text-sm">
              <div className="text-muted-foreground text-xs">รายละเอียดคำขอ</div>
              {ldEntries.length === 0 && filterEntries.length === 0 ? (
                <div className="text-muted-foreground text-sm">ไม่ระบุเงื่อนไขเพิ่มเติม</div>
              ) : (
                <dl className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-x-3 gap-y-1">
                  {ldEntries.map((e) => (
                    <div key={`ld-${e.label}`} className="contents">
                      <dt className="text-muted-foreground">{e.label}</dt>
                      <dd className="font-medium whitespace-pre-wrap">{e.value}</dd>
                    </div>
                  ))}
                  {filterEntries.map((e) => (
                    <div key={e.key} className="contents">
                      <dt className="text-muted-foreground">{e.label}</dt>
                      <dd className="font-medium">{e.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            {/* Status */}
            {data.my_status === 'confirmed' && (
              <div className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-green-800 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                คุณได้ยืนยันรับงานนี้แล้ว
              </div>
            )}
            {data.my_status === 'denied' && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive space-y-1">
                <div className="flex items-center gap-2"><XCircle className="h-4 w-4" /> คุณได้ปฏิเสธงานนี้แล้ว</div>
                {data.my_deny_reason && <div className="text-xs">เหตุผล: {data.my_deny_reason}</div>}
              </div>
            )}
            {data.status !== 'pending' && data.my_status === 'pending' && (
              <div className="flex items-center gap-2 rounded-md border p-3 text-muted-foreground text-sm">
                <AlertCircle className="h-4 w-4" /> คำขอนี้ถูกปิดแล้ว
              </div>
            )}

            {/* Actions */}
            {data.my_status === 'pending' && data.status === 'pending' && (
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3">
                  <Checkbox
                    id="schedule-checked"
                    checked={scheduleChecked}
                    onCheckedChange={(c) => setScheduleChecked(c === true)}
                    disabled={respondedDisabled}
                  />
                  <Label htmlFor="schedule-checked" className="text-sm leading-snug cursor-pointer">
                    ฉันได้ตรวจสอบแล้วว่ามีตารางสอนว่างในวันดังกล่าว
                  </Label>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    className="flex-1"
                    disabled={respondedDisabled || !scheduleChecked}
                    onClick={handleConfirm}
                  >
                    {respond.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    ยืนยันรับงาน
                  </Button>
                  <Button
                    className="flex-1"
                    variant="outline"
                    disabled={respondedDisabled}
                    onClick={() => setDenyOpen(true)}
                  >
                    ปฏิเสธ
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={denyOpen} onOpenChange={setDenyOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ปฏิเสธงานนี้?</AlertDialogTitle>
            <AlertDialogDescription>
              คุณสามารถระบุเหตุผลเพื่อช่วยให้แอดมินจับคู่ครูคนถัดไปได้ดีขึ้น (ไม่บังคับ)
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="เช่น ไม่สะดวกเวลานี้ / มีคิวอื่นแล้ว"
            maxLength={500}
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={respond.isPending}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeny} disabled={respond.isPending}>
              {respond.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              ยืนยันปฏิเสธ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function errorMessage(e: MatchRespondError): string {
  switch (e.error) {
    case 'expired': return 'หมดเวลาตอบรับแล้ว';
    case 'request_closed': return 'คำขอนี้ถูกปิดแล้ว';
    case 'already_responded': return 'คุณได้ตอบคำขอนี้ไปแล้ว';
    case 'not_authorized': return 'คุณไม่มีสิทธิ์ในคำขอนี้';
    default: return e.error || 'เกิดข้อผิดพลาด';
  }
}